import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { 
  Car, Search, Filter, CheckCircle, Clock, AlertTriangle,
  Package, PackageCheck, ChevronDown, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function DossiersView() {
  const [vehicles, setVehicles] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPrestation, setFilterPrestation] = useState("all");
  const [filterPieces, setFilterPieces] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vehiclesRes, prestationsRes, stepsRes] = await Promise.all([
        api.get("/vehicles?include_ongoing=true"),
        api.get("/prestations"),
        api.get("/workflow-steps")
      ]);
      setVehicles(vehiclesRes.data);
      setPrestations(prestationsRes.data);
      setWorkflowSteps(stepsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const getStepLabel = (stepName) => {
    if (stepName === "none") return "Mécanique";
    if (stepName === "termine") return "Terminé";
    const step = workflowSteps.find(s => s.name === stepName);
    return step?.label || stepName;
  };

  const getPrestationLabel = (prestName) => {
    const prest = prestations.find(p => p.name === prestName);
    return prest?.label || prestName;
  };

  const getStatusBadge = (statut, isBlocked) => {
    if (isBlocked) {
      return <Badge className="bg-red-100 text-red-700">Bloqué</Badge>;
    }
    const styles = {
      en_attente: "bg-amber-100 text-amber-700",
      en_cours: "bg-blue-100 text-blue-700",
      termine: "bg-emerald-100 text-emerald-700",
    };
    const labels = {
      en_attente: "En attente",
      en_cours: "En cours",
      termine: "Terminé",
    };
    return <Badge className={styles[statut]}>{labels[statut]}</Badge>;
  };

  // Filtrage
  const filteredVehicles = vehicles.filter(v => {
    // Recherche par immatriculation ou nom client
    const searchMatch = searchTerm === "" || 
      v.immatriculation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.marque.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtre par statut
    const statusMatch = filterStatus === "all" || 
      (filterStatus === "blocked" && v.is_blocked) ||
      (filterStatus !== "blocked" && v.statut === filterStatus && !v.is_blocked);
    
    // Filtre par prestation
    const prestationMatch = filterPrestation === "all" || v.type_intervention === filterPrestation;
    
    // Filtre par pièces
    const piecesMatch = filterPieces === "all" || 
      (filterPieces === "commandees" && v.pieces_commandees) ||
      (filterPieces === "a_commander" && !v.pieces_commandees);
    
    return searchMatch && statusMatch && prestationMatch && piecesMatch;
  });

  // Stats
  const stats = {
    total: vehicles.length,
    en_attente: vehicles.filter(v => v.statut === "en_attente").length,
    en_cours: vehicles.filter(v => v.statut === "en_cours").length,
    termines: vehicles.filter(v => v.statut === "termine").length,
    bloques: vehicles.filter(v => v.is_blocked).length,
    pieces_manquantes: vehicles.filter(v => !v.pieces_commandees && v.statut !== "termine").length,
  };

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="dossiers-view">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight">Dossiers</h1>
        <p className="text-slate-600 mt-1">Liste complète des véhicules et interventions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="border-slate-200 cursor-pointer hover:border-slate-300" onClick={() => setFilterStatus("all")}>
          <CardContent className="p-3 flex items-center gap-3">
            <Car className="h-5 w-5 text-slate-600" />
            <div>
              <p className="text-xl font-bold">{stats.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 cursor-pointer hover:border-amber-300" onClick={() => setFilterStatus("en_attente")}>
          <CardContent className="p-3 flex items-center gap-3">
            <Clock className="h-5 w-5 text-amber-600" />
            <div>
              <p className="text-xl font-bold text-amber-600">{stats.en_attente}</p>
              <p className="text-xs text-slate-500">Attente</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 cursor-pointer hover:border-blue-300" onClick={() => setFilterStatus("en_cours")}>
          <CardContent className="p-3 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-xl font-bold text-blue-600">{stats.en_cours}</p>
              <p className="text-xs text-slate-500">En cours</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 cursor-pointer hover:border-emerald-300" onClick={() => setFilterStatus("termine")}>
          <CardContent className="p-3 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-emerald-600" />
            <div>
              <p className="text-xl font-bold text-emerald-600">{stats.termines}</p>
              <p className="text-xs text-slate-500">Terminés</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 cursor-pointer hover:border-red-300" onClick={() => setFilterStatus("blocked")}>
          <CardContent className="p-3 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <div>
              <p className="text-xl font-bold text-red-600">{stats.bloques}</p>
              <p className="text-xs text-slate-500">Bloqués</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 cursor-pointer hover:border-orange-300" onClick={() => setFilterPieces("a_commander")}>
          <CardContent className="p-3 flex items-center gap-3">
            <Package className="h-5 w-5 text-orange-600" />
            <div>
              <p className="text-xl font-bold text-orange-600">{stats.pieces_manquantes}</p>
              <p className="text-xs text-slate-500">Pièces manq.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher (immat, client, marque...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-dossiers"
              />
            </div>

            {/* Filter by prestation */}
            <Select value={filterPrestation} onValueChange={setFilterPrestation}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Prestation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {prestations.map(p => (
                  <SelectItem key={p.id} value={p.name}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Filter by status */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="termine">Terminé</SelectItem>
                <SelectItem value="blocked">Bloqué</SelectItem>
              </SelectContent>
            </Select>

            {/* Filter by pieces */}
            <Select value={filterPieces} onValueChange={setFilterPieces}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Pièces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="commandees">Pièces OK</SelectItem>
                <SelectItem value="a_commander">Pièces à commander</SelectItem>
              </SelectContent>
            </Select>

            {/* Reset filters */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setSearchTerm("");
                setFilterStatus("all");
                setFilterPrestation("all");
                setFilterPieces("all");
              }}
            >
              Réinitialiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardHeader className="py-3">
          <CardTitle className="text-base">
            {filteredVehicles.length} dossier(s) trouvé(s)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Car className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>Aucun dossier trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Immatriculation</TableHead>
                    <TableHead className="font-semibold">Véhicule</TableHead>
                    <TableHead className="font-semibold">Client</TableHead>
                    <TableHead className="font-semibold">Prestation</TableHead>
                    <TableHead className="font-semibold">Étape</TableHead>
                    <TableHead className="font-semibold">Statut</TableHead>
                    <TableHead className="font-semibold">Pièces</TableHead>
                    <TableHead className="font-semibold">Date RDV</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVehicles.map((vehicle) => (
                    <TableRow 
                      key={vehicle.id} 
                      className={`hover:bg-slate-50 cursor-pointer ${vehicle.is_blocked ? 'bg-red-50' : ''}`}
                      onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                      data-testid={`dossier-row-${vehicle.id}`}
                    >
                      <TableCell className="font-mono font-bold">
                        {vehicle.immatriculation}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{vehicle.marque} {vehicle.modele}</p>
                          {vehicle.couleur && <p className="text-xs text-slate-500">{vehicle.couleur}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{vehicle.client.nom} {vehicle.client.prenom}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{getPrestationLabel(vehicle.type_intervention)}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{getStepLabel(vehicle.workflow_step)}</span>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(vehicle.statut, vehicle.is_blocked)}
                      </TableCell>
                      <TableCell>
                        {vehicle.pieces_commandees ? (
                          <PackageCheck className="h-5 w-5 text-emerald-500" title="Pièces commandées" />
                        ) : (
                          <Package className="h-5 w-5 text-red-500" title="Pièces à commander" />
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {format(parseISO(vehicle.date_rdv), "dd/MM/yyyy", { locale: fr })}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
